import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Search, UserPlus, X } from 'lucide-react';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';
import type { CustomerToken } from '@sleed/bizcommerce-api';
import { Input } from '~/components/ui/input';
import { Button } from '~/components/ui/button';
import { Switch } from '~/components/ui/switch';
import { Pill } from '~/components/pill';
import { customersApi } from '~/lib/api/client';
import { formatCurrency } from '~/lib/dashboard/format';
import { cn } from '~/lib/utils';
import { CustomerCreateForm, type NewCustomerPayload } from './customer-create-dialog';

/** Optional per-customer purchase stats the parent can supply (not present on CustomerToken). */
export interface CustomerStats {
	orderCount?: number;
	lifetimeSpend?: number;
	lastOrderAt?: string | number | Date;
}

interface CustomerPickerProps {
	/** The currently-selected customer, or null when none is chosen. */
	selected: CustomerToken | null;
	onSelect: (customer: CustomerToken) => void;
	onClear: () => void;
	/** Guest mode toggle (hidden when `onGuestChange` is omitted, e.g. bookings need a real customer). */
	guest?: boolean;
	onGuestChange?: (guest: boolean) => void;
	/** Emits the new-customer payload up; the PARENT performs the create. Omit to hide the inline create. */
	onCreateCustomer?: (payload: NewCustomerPayload) => void;
	/** Stats for the selected customer (orders / spend / last order) — hidden when absent. */
	stats?: CustomerStats;
	/** Per-result order-count lookup for the dropdown meta line (optional). */
	resultOrderCount?: (customer: CustomerToken) => number | undefined;
}

const DEBOUNCE_MS = 300;
const TOP = 8;

function initials(c: CustomerToken): string {
	const a = (c.fn ?? '').trim()[0] ?? '';
	const b = (c.ln ?? '').trim()[0] ?? '';
	return (a + b).toUpperCase() || (c.eml?.[0] ?? '?').toUpperCase();
}

function fullName(c: CustomerToken): string {
	return [c.fn, c.ln].filter(Boolean).join(' ').trim() || c.cnm || c.eml;
}

/**
 * Order customer picker (HTML "customer search + inline create"). Async-searches local customers
 * via the generated `CustomersApi.lookup(term, top)` (the only read endpoint it touches; it never
 * mutates). Renders a selected-customer card with avatar + optional stats, a guest-mode toggle,
 * and an inline create form whose payload bubbles up to the parent.
 */
export function CustomerPicker({
	selected,
	onSelect,
	onClear,
	guest = false,
	onGuestChange,
	onCreateCustomer,
	stats,
	resultOrderCount
}: CustomerPickerProps) {
	const { t } = useTranslation();
	const [query, setQuery] = useState('');
	const [results, setResults] = useState<CustomerToken[]>([]);
	const [loading, setLoading] = useState(false);
	const [creating, setCreating] = useState(false);
	const requestSeq = useRef(0);

	useEffect(() => {
		const term = query.trim();
		if (selected || guest || creating || !term) {
			setResults([]);
			setLoading(false);
			return;
		}
		let cancelled = false;
		const seq = ++requestSeq.current;
		setLoading(true);
		const handle = setTimeout(() => {
			customersApi
				.lookup(term, TOP)
				.then(res => {
					if (cancelled || seq !== requestSeq.current) return;
					setResults(res.data ?? []);
				})
				.catch(() => {
					if (!cancelled && seq === requestSeq.current) setResults([]);
				})
				.finally(() => {
					if (!cancelled && seq === requestSeq.current) setLoading(false);
				});
		}, DEBOUNCE_MS);
		return () => {
			cancelled = true;
			clearTimeout(handle);
		};
	}, [query, selected, guest, creating]);

	// Selected-customer card
	if (selected && !guest) {
		return (
			<div className="rounded-lg border border-border bg-surface p-4">
				<div className="flex items-start gap-3">
					<span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-subtle text-sm font-semibold text-brand-subtle-fg">
						{initials(selected)}
					</span>
					<div className="min-w-0 flex-1">
						<div className="text-sm font-semibold text-foreground">{fullName(selected)}</div>
						<div className="truncate text-[11px] text-brand-subtle-fg">{selected.eml}</div>
						{(selected.mob || selected.tel) && (
							<div className="mt-0.5 text-[11px] tabular-nums text-foreground-muted">{selected.mob ?? selected.tel}</div>
						)}
					</div>
					<Button type="button" variant="ghost" size="icon-sm" aria-label={t('common.remove')} onClick={onClear}>
						<X size={16} />
					</Button>
				</div>

				{stats && (stats.orderCount != null || stats.lifetimeSpend != null || stats.lastOrderAt != null) && (
					<div className="mt-3 grid grid-cols-2 gap-3 border-t border-border-subtle pt-3">
						{stats.orderCount != null && <Stat label={t('orders.components.customerPicker.statOrders')}>{stats.orderCount}</Stat>}
						{stats.lifetimeSpend != null && (
							<Stat label={t('orders.components.customerPicker.statSpend')}>{formatCurrency(stats.lifetimeSpend)}</Stat>
						)}
						{stats.lastOrderAt != null && (
							<Stat label={t('orders.components.customerPicker.statLastOrder')} className="col-span-2">
								<span className="text-sm tabular-nums">{format(new Date(stats.lastOrderAt), 'd MMM yyyy, HH:mm', { locale: el })}</span>
							</Stat>
						)}
					</div>
				)}
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-3">
			{onGuestChange && (
				<div className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface px-4 py-2.5">
					<span className="text-sm font-medium text-foreground">{t('orders.components.customerPicker.guestToggle')}</span>
					<Switch checked={guest} onCheckedChange={onGuestChange} aria-label={t('orders.components.customerPicker.guestToggle')} />
				</div>
			)}

			{!guest && (
				<>
					<div className="relative">
						<Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted" />
						<Input
							value={query}
							onChange={e => setQuery(e.target.value)}
							placeholder={t('orders.components.customerPicker.searchPlaceholder')}
							className="pl-9"
							disabled={creating}
						/>
					</div>

					{!creating && query.trim() !== '' && (
						<div className="overflow-hidden rounded-md border border-border">
							{loading ? (
								<div className="flex items-center justify-center gap-2 py-6 text-sm text-foreground-muted">
									<Loader2 size={16} className="animate-spin" />
									{t('common.search')}…
								</div>
							) : (
								<>
									{results.length > 0 && (
										<>
											<div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-foreground-subtle">
												{t('orders.components.customerPicker.localGroup', { count: results.length })}
											</div>
											<ul className="divide-y divide-border-subtle">
												{results.map(c => {
													const oc = resultOrderCount?.(c);
													return (
														<li key={c.id ?? c.eml}>
															<button
																type="button"
																onClick={() => {
																	onSelect(c);
																	setQuery('');
																}}
																className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-muted/50">
																<span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-foreground-muted">
																	{initials(c)}
																</span>
																<div className="min-w-0 flex-1">
																	<div className="truncate text-sm font-medium text-foreground">{fullName(c)}</div>
																	<div className="truncate text-[11px] text-foreground-muted">
																		{[c.eml, c.mob ?? c.tel, oc != null && t('orders.components.customerPicker.orders', { count: oc })]
																			.filter(Boolean)
																			.join(' · ')}
																	</div>
																</div>
																<Pill variant="neutral">{t('orders.components.customerPicker.local')}</Pill>
															</button>
														</li>
													);
												})}
											</ul>
										</>
									)}
									{results.length === 0 && !onCreateCustomer && (
										<div className="px-3 py-4 text-center text-sm text-foreground-muted">{t('common.noResults')}</div>
									)}
									{onCreateCustomer && (
										<button
											type="button"
											onClick={() => setCreating(true)}
											className="flex w-full items-center gap-2 border-t border-border-subtle px-3 py-2.5 text-left text-sm text-brand-subtle-fg hover:bg-brand-subtle/40">
											<UserPlus size={14} />
											{t('orders.components.customerPicker.createNamed', { name: query.trim() })}
										</button>
									)}
								</>
							)}
						</div>
					)}

					{creating && onCreateCustomer && (
						<CustomerCreateForm
							initialName={query}
							onUse={payload => {
								onCreateCustomer(payload);
								setCreating(false);
							}}
							onCancel={() => setCreating(false)}
						/>
					)}
				</>
			)}
		</div>
	);
}

function Stat({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
	return (
		<div className={cn(className)}>
			<div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-foreground-subtle">{label}</div>
			<div className="text-sm font-semibold text-foreground">{children}</div>
		</div>
	);
}
